import { useMemo } from 'react';
import { useListWorkspacesInfiniteQuery } from '@frontend/api-workspaces';
import { useLocationGroupStore } from '@frontend/scope';
import { useWorkspaceFeature, useWorkspaceShallowStore } from '@frontend/workspace-scope';

export type WorkspaceInfo = {
  isWorkspaceMode: boolean;
  workspaceName: string;
  locationCount: number;
};

/**
 * Custom hook that resolves the current workspace info (name, location count,
 * whether workspace mode is active). Call this inside leaf components that
 * need workspace data
 *
 */
export const useWorkspaceInfo = (): WorkspaceInfo => {
  const { isWorkspaceMode } = useWorkspaceFeature();
  const { selectedWorkspaceId } = useWorkspaceShallowStore('selectedWorkspaceId');
  const selectedOrgId = useLocationGroupStore((state) => state.selectedOrgId);

  const { data: workspacesData } = useListWorkspacesInfiniteQuery(selectedOrgId, {
    enabled: isWorkspaceMode && !!selectedWorkspaceId,
  });

  const selectedWorkspace = useMemo(() => {
    if (!isWorkspaceMode || !selectedWorkspaceId) return undefined;
    const allWorkspaces = workspacesData?.pages.flatMap((p) => p.workspaces ?? []) ?? [];
    return allWorkspaces.find((w) => w.workspaceId === selectedWorkspaceId);
  }, [isWorkspaceMode, selectedWorkspaceId, workspacesData]);

  const workspaceName = selectedWorkspace?.workspaceName ?? '';
  const locationCount = selectedWorkspace?.locationIds?.length ?? 0;

  return useMemo(
    () => ({ isWorkspaceMode, workspaceName, locationCount }),
    [isWorkspaceMode, workspaceName, locationCount]
  );
};
